import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowUpRight, Check } from "lucide-react";
import { SERVICES, PROCESS_STEPS, FAQS } from "../lib/data";
import CTASection from "../components/CTASection";
import { fadeUp } from "../lib/motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Sparkles;
  return <Cmp className={className} aria-hidden="true" />;
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);
  const heroImageSrc = service.hero_image?.startsWith("http") || service.hero_image?.startsWith("/")
    ? service.hero_image
    : `/${service.hero_image}`;

  return (
    <>
      <section className="relative overflow-hidden pt-10 pb-16 md:pt-16 md:pb-24">
        <div className="absolute inset-0 subtle-radial" aria-hidden="true" />
        <div className="container-page relative grid lg:grid-cols-12 gap-12 items-center">
          <motion.div {...fadeUp} className="lg:col-span-7">
            <Link to="/services" className="badge-pill mb-6 inline-flex" data-testid="service-back">← All services</Link>
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center text-blue-600 dark:text-blue-400">
                <Icon name={service.icon} className="h-5 w-5" />
              </div>
              <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">{service.title}</p>
            </div>
            <h1 className="mt-6 text-4xl md:text-6xl font-extrabold tracking-tighter text-slate-900 dark:text-white leading-[1.02]">
              {service.tagline}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 dark:text-slate-300 leading-relaxed">
              {service.short}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="btn-primary" data-testid="service-cta-primary">
                Ask more about {service.title} <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div className="rounded-[24px] overflow-hidden border border-slate-200 dark:border-slate-800 shadow-xl">
              <img src={heroImageSrc} alt={service.title} className="w-full h-[380px] object-cover" loading="lazy" />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white dark:bg-slate-950 border-y border-slate-100 dark:border-slate-900" data-testid="service-benefits">
        <div className="container-page grid lg:grid-cols-2 gap-12">
          <motion.div {...fadeUp}>
            <p className="eyebrow">Benefits</p>
            <h2 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tighter text-slate-900 dark:text-white">
              What you actually get out of this.
            </h2>
            <ul className="mt-8 space-y-4">
              {service.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="h-6 w-6 rounded-full bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center mt-0.5 flex-none">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-slate-700 dark:text-slate-200">{b}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fadeUp}>
            <p className="eyebrow">What's included</p>
            <h2 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tighter text-slate-900 dark:text-white">
              Features, in plain English.
            </h2>
            <ul className="mt-8 space-y-4">
              {service.features.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <span className="h-6 w-6 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-300 flex items-center justify-center mt-0.5 flex-none text-xs font-semibold">•</span>
                  <span className="text-slate-700 dark:text-slate-200">{f}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* <section className="section bg-slate-50 dark:bg-slate-950/50 border-y border-slate-100 dark:border-slate-900" data-testid="service-process">
        <div className="container-page">
          <div className="max-w-3xl">
            <p className="eyebrow">Our process</p>
            <h2 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tighter text-slate-900 dark:text-white">
              How this actually happens.
            </h2>
          </div>
          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {PROCESS_STEPS.map((step) => (
              <div key={step.step} className="card-surface p-7">
                <span className="text-sm font-semibold tracking-[0.18em] text-blue-600 dark:text-blue-400">STEP {step.step}</span>
                <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">{step.title}</h3>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      <section className="section" data-testid="service-faq">
        <div className="container-page max-w-3xl">
          <p className="eyebrow">Questions</p>
          <h2 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tighter text-slate-900 dark:text-white">Frequently asked</h2>
          <div className="mt-10">
            <Accordion type="single" collapsible className="space-y-3">
              {FAQS.slice(0, 5).map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="card-surface px-6 border">
                  <AccordionTrigger className="text-left py-5 hover:no-underline font-semibold text-slate-900 dark:text-white">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 dark:text-slate-400 pb-5 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="pb-8" data-testid="service-related">
        <div className="container-page">
          <p className="eyebrow">Related services</p>
          <h2 className="mt-4 text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">You might also like</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to={`/services/${r.slug}`} className="card-surface card-hover p-6 group" data-testid={`related-${r.slug}`}>
                <div className="h-10 w-10 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center text-blue-600 dark:text-blue-400">
                  <Icon name={r.icon} className="h-4 w-4" />
                </div>
                <h3 className="mt-4 font-semibold text-slate-900 dark:text-white group-hover:text-blue-600">{r.title}</h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{r.tagline}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
